import React, { useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import Loader from './Loader';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort, faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";

// Reusable TanStack Table Component
// Props:
// - data: Array of data to display
// - columns: Column definitions for the table
// - loading: Show loader when true
// - noDataMessage: Custom message when no data (default: "No Data Found")
// - enableSorting: Enable sorting globally (default: true)
// - tableClassName: Additional CSS classes for table
// - initialSorting: Initial sorting state (default: [])
const TanStackTable = ({
  data = [],
  columns = [],
  loading = false,
  noDataMessage = "No Data Found",
  enableSorting = true,
  tableClassName = "table table-bordered table-hover",
  initialSorting = [],
  rowStyle = null
}) => {
  const [sorting, setSorting] = useState(initialSorting);
  const [showNoData, setShowNoData] = useState(false);

  useEffect(() => {
    let timer;
    if (!loading && (!data || data.length === 0)) {
      timer = setTimeout(() => setShowNoData(true), 500);
    } else {
      setShowNoData(false);
    }
    return () => clearTimeout(timer);
  }, [loading, data]);

  const table = useReactTable({
    data: data || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting,
  });

  if (loading) {
    return (
      <div className="text-center p-4">
        <Loader />
      </div>
    );
  }

  const sortActiveStyle = { marginLeft: '10px 0 10px 5px', fontSize: '0.65rem' };
  const sortInactiveStyle = { marginLeft: '10px 0 10px 5px', fontSize: '0.65rem', color: '#ccc' };

  return (
    <div className="table-responsive">
      <table className={tableClassName} style={{ tableLayout: 'fixed', width: '100%' }}>
        <thead className="table-light">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  style={{
                    width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : 'auto',
                    verticalAlign: 'top',
                    cursor: header.column.getCanSort() ? 'pointer' : 'default'
                  }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getCanSort() && (
                      <span>
                        {{
                          asc: <FontAwesomeIcon icon={faSortUp} style={sortActiveStyle} />,
                          desc: <FontAwesomeIcon icon={faSortDown} style={sortActiveStyle} />,
                        }[header.column.getIsSorted()] ?? <FontAwesomeIcon icon={faSort} style={sortInactiveStyle} />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row, rowIndex) => (
              <tr 
              key={row.id}
              style={rowStyle ? rowStyle(row.original, rowIndex) : {}}
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    style={{
                      width: cell.column.columnDef.size ? `${cell.column.columnDef.size}px` : 'auto',
                      wordBreak: 'break-word',
                      verticalAlign: 'middle'
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center p-4 text-danger font-weight-bold">
                {showNoData ? noDataMessage : null}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

};

export default TanStackTable;
